import React, { useState, useEffect, useRef, useCallback } from "react";
import { getAllSports, addSportToUser } from "../../services/api/sportsService";
import { updateUserProfile } from "../../services/api/userService";
import { dedupeCitySuggestions, formatCityLabel } from "../../utils/citySuggestions";
import "./SportsOnboarding.css";

const ONBOARDING_KEY = "sports_onboarding_done";

const MIN_CITY_CHARS = 3;

/**
 * SportsOnboardingModal — shown once after first login if user has 0 sports.
 *
 * Step 1 — Pick Your Sports
 * Step 2 — Set Your Home City  (optional but encouraged)
 *
 * Dismissed by choosing at least 1 sport, or permanently skipped after 3 times.
 */
function SportsOnboardingModal({ onComplete }) {
  // ── Step 1: sport selection ───────────────────────────────────────────────
  const [step, setStep]       = useState(1);
  const [sports, setSports]   = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");

  // ── Step 2: city picker ───────────────────────────────────────────────────
  const [cityInput, setCityInput]             = useState('');
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [cityLoading, setCityLoading]         = useState(false);
  const [cityChosen, setCityChosen]           = useState(null); // { lat, lng, label }
  const [savingCity, setSavingCity]           = useState(false);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [gpsLoading, setGpsLoading]           = useState(false);
  const [gpsError, setGpsError]               = useState('');
  const debounceRef = useRef(null);
  const abortRef    = useRef(null);

  useEffect(() => {
    getAllSports({ limit: 50 })
      .then((data) => {
        const list = data?.data?.sports || data?.sports || data?.data || [];
        setSports(list.filter((s) => s.is_active !== false));
      })
      .catch(() => setError("Failed to load sports"))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Step 1 → Step 2 ──────────────────────────────────────────────────────

  const handleSaveSports = async () => {
    if (selected.size === 0) {
      setError("Please select at least one sport.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await Promise.all(
        [...selected].map((id) => addSportToUser(null, id, { skill_level: "beginner" }))
      );
      setStep(2); // Proceed to city picker
    } catch {
      setError("Failed to save sports selection. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSkipSports = () => {
    // Skip is no longer offered — kept as internal fallback only.
    localStorage.setItem(ONBOARDING_KEY, "1");
    if (onComplete) onComplete([]);
  };

  // ── Step 2: city autocomplete (city-level only) ───────────────────────────

  const fetchCitySuggestions = useCallback((q) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;
    if (!apiKey || q.trim().length < MIN_CITY_CHARS) {
      setCitySuggestions([]);
      setCityLoading(false);
      return;
    }

    setCityLoading(true);
    // type=city ensures only city-level results are returned, not street addresses.
    // A city-level geocode gives a centroid that works well as a search radius origin.
    const url =
      `https://api.geoapify.com/v1/geocode/autocomplete` +
      `?text=${encodeURIComponent(q)}&limit=6&type=city&apiKey=${apiKey}`;

    fetch(url, { signal: abortRef.current.signal })
      .then((r) => r.json())
      .then((data) => {
        setCityLoading(false);
        setCitySuggestions(dedupeCitySuggestions(data?.features));
      })
      .catch((err) => {
        if (err.name !== 'AbortError') setCityLoading(false);
      });
  }, []);

  const handleCityInput = (e) => {
    const q = e.target.value;
    setCityInput(q);
    setCityChosen(null);
    setShowCitySuggestions(true);
    if (!q) { setCitySuggestions([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchCitySuggestions(q), 400);
  };

  const handleCitySelect = (feature) => {
    const p = feature.properties;
    const label = formatCityLabel(p);
    setCityInput(label);
    setCityChosen({ lat: p.lat, lng: p.lon, label });
    setCitySuggestions([]);
    setShowCitySuggestions(false);
  };

  // Use GPS as home location
  const handleUseGps = () => {
    if (!navigator.geolocation) {
      setGpsError('Geolocation is not supported by your browser.');
      return;
    }
    setGpsLoading(true);
    setGpsError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCityChosen({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          label: 'Current Location',
        });
        setCityInput('Current Location (GPS)');
        setGpsLoading(false);
      },
      () => {
        setGpsError('Location access denied. Please search for your city instead.');
        setGpsLoading(false);
      },
      { timeout: 10000 }
    );
  };

  const handleSaveCity = async () => {
    if (!cityChosen) return;
    setSavingCity(true);
    try {
      // Also store the picked sports as the profile's Favourite Sports
      // (BUG-40). /users/profile only changes the fields sent here.
      const favoriteSports = sports
        .filter((s) => selected.has(s.id))
        .map((s) => s.name)
        .join(', ');
      await updateUserProfile({
        home_city: cityChosen.label,
        home_lat: cityChosen.lat,
        home_lng: cityChosen.lng,
        ...(favoriteSports ? { favoriteSports } : {}),
      });
    } catch {
      // Non-critical — carry on even if the save fails
    } finally {
      setSavingCity(false);
      localStorage.setItem(ONBOARDING_KEY, "1");
      if (onComplete) onComplete([...selected]);
    }
  };

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-modal">

        {/* ── Step 1: Pick Sports ────────────────────────────────────── */}
        {step === 1 && (
          <>
            <div className="onboarding-header">
              <span className="onboarding-icon">🏆</span>
              <h2>Pick Your Sports</h2>
              <p>Select at least one sport you play or want to follow. You can change this anytime.</p>
            </div>

            {loading && <p className="onboarding-loading">Loading sports…</p>}

            {!loading && (
              <div className="onboarding-grid">
                {sports.map((sport) => (
                  <button
                    key={sport.id}
                    className={`onboarding-sport-tile ${selected.has(sport.id) ? "selected" : ""}`}
                    onClick={() => toggle(sport.id)}
                    type="button"
                    data-testid={`sport-tile-${sport.id}`}
                  >
                    <span className="sport-emoji">{SPORT_EMOJI[sport.name] || "🎯"}</span>
                    <span className="sport-name">{sport.name}</span>
                    {selected.has(sport.id) && <span className="sport-check">✓</span>}
                  </button>
                ))}
              </div>
            )}

            {error && <p className="onboarding-error">{error}</p>}

            <div className="onboarding-actions">
              <button
                className="onboarding-btn primary"
                onClick={handleSaveSports}
                disabled={saving || loading || selected.size === 0}
                data-testid="btn-continue-sports"
              >
                {saving ? "Saving…" : `Continue with ${selected.size} sport${selected.size !== 1 ? "s" : ""}`}
              </button>
            </div>
          </>
        )}

        {/* ── Step 2: Set Home City ──────────────────────────────────── */}
        {step === 2 && (
          <>
            <div className="onboarding-header">
              <span className="onboarding-icon">📍</span>
              <h2>Where are you based?</h2>
              <p>
                Setting your home city lets the app automatically show nearby Drop-Ins,
                Teams, Leagues, Tournaments and Matches. You can update this anytime in
                your profile.
              </p>
            </div>

            <div className="onboarding-city-picker" data-testid="city-picker">
              {/* GPS option */}
              <button
                type="button"
                className="onboarding-btn secondary"
                style={{ marginBottom: '0.75rem', width: '100%' }}
                onClick={handleUseGps}
                disabled={gpsLoading || savingCity}
                data-testid="btn-use-gps"
              >
                {gpsLoading ? '… Getting location' : '📍 Use My Current Location'}
              </button>
              {gpsError && <p className="onboarding-error" style={{ marginTop: 0 }}>{gpsError}</p>}

              <p style={{ textAlign: 'center', margin: '0.5rem 0', fontSize: '0.82rem', color: '#94a3b8' }}>or search your city</p>

              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className="onboarding-city-input"
                  placeholder="Search your city or district…"
                  value={cityInput}
                  onChange={handleCityInput}
                  onFocus={() => setShowCitySuggestions(true)}
                  onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                  autoComplete="off"
                  data-testid="onboarding-city-input"
                />
                {cityLoading && (
                  <span className="onboarding-city-spinner">…</span>
                )}

                {showCitySuggestions && citySuggestions.length > 0 && (
                  <ul className="onboarding-city-suggestions" data-testid="onboarding-city-suggestions">
                    {citySuggestions.map((feature, i) => {
                      const p = feature.properties;
                      const label = formatCityLabel(p);
                      return (
                        <li
                          key={p.place_id || i}
                          onMouseDown={() => handleCitySelect(feature)}
                          data-testid={`onboarding-city-suggestion-${i}`}
                        >
                          📍 {label}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {cityChosen && (
                <p className="onboarding-city-chosen" data-testid="city-chosen-label">
                  ✅ {cityChosen.label}
                </p>
              )}
            </div>

            <div className="onboarding-actions">
              <button
                className="onboarding-btn primary"
                onClick={handleSaveCity}
                disabled={savingCity || !cityChosen}
                data-testid="btn-save-city"
              >
                {savingCity ? "Saving…" : "Save & Get Started"}
              </button>
            </div>

            <p style={{ textAlign: 'center', fontSize: '0.8em', color: '#94a3b8', marginTop: '0.5rem' }}>
              You can set or change your location anytime from your profile.
            </p>
          </>
        )}

      </div>
    </div>
  );
}

const SPORT_EMOJI = {
  Football: "⚽",
  Basketball: "🏀",
  Volleyball: "🏐",
  Cricket: "🏏",
  Tennis: "🎾",
  Badminton: "🏸",
  "Table Tennis": "🏓",
  Swimming: "🏊",
  Rugby: "🏉",
  Hockey: "🏑",
  Baseball: "⚾",
  Golf: "⛳",
  Boxing: "🥊",
  Athletics: "🏃",
  Cycling: "🚴",
};

export { ONBOARDING_KEY };
export default SportsOnboardingModal;
